import React, { useEffect, useState } from 'react';
import { useSession } from './SessionContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { loadAppSettings } from './appSettings';
import { buildPilotStatusSummary, loadPilotCompliance } from './pilotStatus';
import DashboardNoticeBoard from './dashboard/DashboardNoticeBoard';
import { useOrganisationSubscriptionTier } from './useOrganisationSubscriptionTier';
import { fetchOfficialTechLogsForBookings, indexOfficialTechLogs } from './officialTechLog';
import DashboardNextFlightBrief from './dashboard/DashboardNextFlightBrief';
import DashboardFlightFlow from './dashboard/DashboardFlightFlow';
import DashboardPreviousFlightBrief from './dashboard/DashboardPreviousFlightBrief';
import DashboardHero from './dashboard/DashboardHero';
import DashboardMainContent from './dashboard/DashboardMainContent';
import OrganisationContextSelector from './OrganisationContextSelector';
import { selectPreviousFlightCard } from './dashboard/previousFlightSelection';
import wingitDesign from './designSystem';
import { fetchAircraftDocuments } from './admin/services/aircraftDocumentsService';
import { getAircraftDocumentStatus } from './preflight/documentStatus';
import { isOutstandingPaymentBooking } from './admin/paymentUtils';
import {
  getActiveNextOverdueCandidates,
  getBookingUserIdCandidates,
  getResolvedPostFlightStatus,
  hasSavedPilotLogbookEntry,
  isPostFlightCompleted,
} from './booking/activeFlightSelection';
import { MEDIA_QUERIES, useMediaQuery } from './responsive';
import './pilotPagesMobile.css';

export function selectDashboardWorkflowBookings({
  activeBooking,
  nextFutureBooking,
  overdueBooking,
  postCompleteBooking,
}) {
  const currentBooking =
    activeBooking || nextFutureBooking || null;

  return {
    workflowBooking: currentBooking,
  };
}

function normalizeRole(...candidates) {
  for (const candidate of candidates) {
    const value = String(candidate || '').trim().toLowerCase();
    if (value) return value;
  }
  return '';
}

function collectRoles(...candidates) {
  return candidates.map(candidate => String(candidate || '').trim().toLowerCase()).filter(Boolean);
}

function isOwnerByEmail(email) {
  return String(email || '').trim().toLowerCase() === 'owner@demo.wingit.app';
}

function getStatusAppearance(status) {
  if (status === 'green') {
    return { label: 'CURRENT', color: '#2fd37d', background: 'rgba(47,211,125,0.10)', border: 'rgba(47,211,125,0.35)' };
  }
  if (status === 'amber') {
    return { label: 'DUE SOON', color: '#f2b84b', background: 'rgba(242,184,75,0.10)', border: 'rgba(242,184,75,0.35)' };
  }
  return { label: 'ACTION REQUIRED', color: '#f26d6d', background: 'rgba(242,109,109,0.10)', border: 'rgba(242,109,109,0.35)' };
}





function parseDateTime(dateValue, timeValue) {
  if (!dateValue) return null;
  const parsed = new Date(`${dateValue}T${String(timeValue || '00:00').trim() || '00:00'}`);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function addMinutes(date, minutes) {
  return new Date(date.getTime() + Number(minutes || 0) * 60000);
}

function getBookingEndDate(booking) {
  const explicitEnd = parseDateTime(booking.end_date, booking.end_time);
  if (explicitEnd) return explicitEnd;

  const start = parseDateTime(booking.date, booking.time);
  if (!start) return null;

  return addMinutes(start, Number(booking.duration) || 60);
}

function isMissingDashboardBookingColumnsError(error) {
  const message = String(error?.message || '').toLowerCase();

  return (
    message.includes('schema cache') ||
    message.includes('column') ||
    message.includes('purpose') ||
    message.includes('tech_log_status') ||
    message.includes('end_date') ||
    message.includes('end_time') ||
    message.includes('departure_airfield') ||
    message.includes('destination_airfield')
  );
}

function logDashboardBookingQueryError(error, stage) {
  if (!error) return;

  console.error('[Dashboard bookings query error]', {
    stage,
    message: error?.message || '',
    code: error?.code || '',
    details: error?.details || '',
    hint: error?.hint || '',
  });
}

function isTechLogComplete(
  booking,
  techLogEntry = {}
) {
  const hasOfficialEntry =
    techLogEntry &&
    typeof techLogEntry === 'object' &&
    Object.keys(techLogEntry).length > 0;

  return (
    hasOfficialEntry ||
    Boolean(
      getResolvedPostFlightStatus(booking)
    )
  );
}

function isPaymentComplete(booking) {
  const status = String(booking?.payment_status || '').toLowerCase();
  return status === 'paid' || status === 'not_required';
}

function getFlightClosureState(
  booking,
  techLogEntry = {}
) {
  const endDate = getBookingEndDate(booking);
  const hasEnded = endDate ? endDate <= new Date() : false;

  if (!hasEnded) return 'scheduled';
    if (!isTechLogComplete(booking, techLogEntry)) {
    return 'tech_outstanding';
  }
  if (!isPaymentComplete(booking)) return 'payment_outstanding';
  return 'closed';
}

function getDefectDetails(entry) {
  const details = String(entry?.defect || entry?.snag || '').trim();
  if (!details || details.toLowerCase() === 'nil') return '';
  return details;
}

function hasOpenDefect(entry) {
  const status = String(entry?.defectStatus || entry?.defect_status || '').trim().toLowerCase();
  const aircraftStatus = String(entry?.aircraftStatus || '').trim().toLowerCase();
  const details = getDefectDetails(entry);

  return status === 'open' || aircraftStatus === 'defect_reported' || !!details;
}

function isBookingCancelledOrDeleted(booking) {
  const status = String(booking?.status || '').trim().toLowerCase();
  const cancelledAt = String(booking?.cancelled_at || '').trim();
  const deletedAt = String(booking?.deleted_at || '').trim();

  return (
    booking?.is_deleted === true ||
    deletedAt.length > 0 ||
    cancelledAt.length > 0 ||
    status === 'cancelled' ||
    status === 'canceled' ||
    status === 'deleted'
  );
}

function getOrganisationPackageLabel(tier) {
  const value = String(tier || '').trim().toLowerCase();

  if (value === 'calendar-logbook-techlog') {
    return 'Calendar + logbooks — £19.99/month';
  }

  if (value === 'full-package') {
    return 'Full package — £29.99/month';
  }

  return 'Calendar booking — Free';
}

export default function Dashboard() {
  const session = useSession();
  const user = session?.user;
  const userMeta = user?.user_metadata || {};
  const sessionOrganisationId =
    session?.profile?.organisation_id ||
    user?.organisation_id ||
    userMeta?.organisation_id ||
    '';
  const navigate = useNavigate();
  const location = useLocation();
  const {
    organisationSubscriptionTier,
    selectedOrganisationSubscriptionTier,
    organisationTrialDaysRemaining,
    organisationTrialActive,
    organisationTrialWarningLevel,
  } = useOrganisationSubscriptionTier(user);

  const isMobile = useMediaQuery(MEDIA_QUERIES.phone);
  const [resolvedRole, setResolvedRole] = useState(normalizeRole(userMeta.role, user?.app_metadata?.role, user?.role));
  const [statusRows, setStatusRows] = useState([]);
  const [dbCompliance, setDbCompliance] = useState({});
  const [profileData, setProfileData] = useState({});
  const [organisationId, setOrganisationId] =
    useState('');
  const [nextFlight, setNextFlight] = useState(null);
  const [workflowBooking, setWorkflowBooking] = useState(null);
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [operationsError, setOperationsError] = useState('');
  const [operations, setOperations] = useState({
    openDefects: [],
    techOutstanding: [],
    paymentOutstanding: [],
    documentAttention: [],
    closed: [],
  });
  const [postFlightBacklog, setPostFlightBacklog] = useState([]);
  const [previousFlightBooking, setPreviousFlightBooking] =
    useState(null);

  const styles = getStyles(isMobile);

  useEffect(() => {
    let cancelled = false;

    async function resolveProfile() {
      if (!user?.id) {
        if (!cancelled) {
          setResolvedRole('');
          setDbCompliance({});
          setProfileData({});
          setOrganisationId('');
          setNextFlight(null);
          setWorkflowBooking(null);
          setPostFlightBacklog([]);
          setPreviousFlightBooking(null);
        }
        return;
      }

      const { data } = await supabase
        .from('users')
        .select('full_name, email, phone, role, licence_type, org_name, home_airfield, licence_expiry, medical_expiry, last_landing_date, night_currency_expiry, check_flight_due, organisation_id')
        .eq('id', user.id)
        .maybeSingle();

      if (!cancelled) {
        setResolvedRole(normalizeRole(data?.role, user?.user_metadata?.role, user?.app_metadata?.role, user?.role));
        setOrganisationId(
          String(
            data?.organisation_id ||
            user?.user_metadata?.organisation_id ||
            user?.app_metadata?.organisation_id ||
            ''
          ).trim()
        );

        setProfileData({
          fullName: data?.full_name || userMeta.full_name || '',
          phone: data?.phone || userMeta.phone || '',
          licenceType: data?.licence_type || userMeta.licence_type || '',
          homeAirfield: data?.home_airfield || userMeta.home_airfield || '',
          orgName: data?.org_name || userMeta.org_name || '',
        });

        setDbCompliance({
          licenceExpiry: data?.licence_expiry || '',
          medicalExpiry: data?.medical_expiry || '',
          lastLandingDate: data?.last_landing_date || '',
          nightCurrencyExpiry: data?.night_currency_expiry || '',
          checkFlightDue: data?.check_flight_due || '',
        });
      }

      const bookingUserIdCandidates = await getBookingUserIdCandidates(
        supabase,
        user.id
      );

      if (bookingUserIdCandidates.length === 0) {
        if (!cancelled) {
          setNextFlight(null);
          setWorkflowBooking(null);
          setPostFlightBacklog([]);
          setPreviousFlightBooking(null);
        }

        return;
      }

      let bookingRows = [];

      const dashboardBookingsQuery = supabase
        .from('bookings')
        .select('id, user_id, aircraft_id, date, time, duration, end_date, end_time, purpose, briefing_data, tech_log_status')
        .in('user_id', bookingUserIdCandidates)
        .order('date', { ascending: true })
        .order('time', { ascending: true })
        .limit(120);

      let { data: primaryRows, error: primaryError } =
        await dashboardBookingsQuery;

      if (primaryError) {
        logDashboardBookingQueryError(
          primaryError,
          'primary_select'
        );
      }

      if (
        primaryError &&
        isMissingDashboardBookingColumnsError(
          primaryError
        )
      ) {
        const fallbackWithEndColumns = await supabase
          .from('bookings')
          .select('id, user_id, aircraft_id, date, time, duration, end_date, end_time, notes, briefing_data, tech_log_status, tech_log_completed_at')
          .in('user_id', bookingUserIdCandidates)
          .order('date', { ascending: true })
          .order('time', { ascending: true })
          .limit(120);

        if (fallbackWithEndColumns.error) {
          logDashboardBookingQueryError(
            fallbackWithEndColumns.error,
            'fallback_with_end_columns'
          );
        }

        if (
          fallbackWithEndColumns.error &&
          isMissingDashboardBookingColumnsError(
            fallbackWithEndColumns.error
          )
        ) {
          const fallbackMinimal = await supabase
            .from('bookings')
            .select('id, user_id, aircraft_id, date, time, duration, notes, briefing_data, tech_log_status, tech_log_completed_at')
            .in('user_id', bookingUserIdCandidates)
            .order('date', { ascending: true })
            .order('time', { ascending: true })
            .limit(120);

          if (fallbackMinimal.error) {
            logDashboardBookingQueryError(
              fallbackMinimal.error,
              'fallback_minimal'
            );
          }

          primaryRows = (fallbackMinimal.data || []).map(
            (row) => ({
              ...row,
              end_date: null,
              end_time: null,
              purpose: row?.notes || '',
              tech_log_status:
                row?.tech_log_status || '',
            })
          );
          primaryError = fallbackMinimal.error;
        } else {
          primaryRows = (
            fallbackWithEndColumns.data || []
          ).map((row) => ({
            ...row,
            purpose: row?.notes || '',
            tech_log_status:
              row?.tech_log_status || '',
          }));
          primaryError =
            fallbackWithEndColumns.error;
        }
      }

      if (primaryError) {
        if (!cancelled) {
          setNextFlight(null);
        }
        return;
      }

      bookingRows = primaryRows || [];

      const bookingIds = bookingRows
        .map((booking) => String(booking?.id || '').trim())
        .filter(Boolean);

      if (bookingIds.length > 0) {
        const {
          data: officialTechRows,
          error: officialTechError,
        } = await fetchOfficialTechLogsForBookings(
          bookingIds
        );

        if (!officialTechError) {
          const indexed = indexOfficialTechLogs(
            officialTechRows || []
          );

          bookingRows = bookingRows.map((booking) => {
            const entry =
              indexed.logMap?.[
                String(booking?.id || '')
              ] || null;

            if (!hasSavedPilotLogbookEntry(entry, booking?.tech_log_status)) {
              return booking;
            }

            return {
              ...booking,
              pilot_logbook_status:
                booking?.pilot_logbook_status ||
                'completed',
              pilot_logbook_saved: true,
            };
          });
        }
      }

      const now = new Date();

      let postFlightAircraftMap = {};

      const bookingAircraftIds = [
        ...new Set(
          (bookingRows || [])
            .map((row) => row?.aircraft_id)
            .filter(Boolean)
        ),
      ];

      const missingAircraftIds = bookingAircraftIds.filter(
        (aircraftId) =>
          !postFlightAircraftMap[String(aircraftId)]
      );

      if (missingAircraftIds.length > 0) {
        const { data: missingAircraftRows } = await supabase
          .from('aircraft')
          .select('id, registration, icao_type, image_url')
          .in('id', missingAircraftIds);

        postFlightAircraftMap = {
          ...postFlightAircraftMap,
          ...((missingAircraftRows || []).reduce((map, row) => {
            map[String(row.id)] = row;
            return map;
          }, {})),
        };
      }

      const sourceRows = bookingRows || [];

      const outstandingPostFlights = sourceRows
        .filter((booking) => {
          const end = getBookingEndDate(booking);
          return Boolean(end && end < now);
        })
        .filter((booking) => !isBookingCancelledOrDeleted(booking))
        .filter((booking) => !isPostFlightCompleted(booking))
        .sort((left, right) => {
          const leftEnd = getBookingEndDate(left);
          const rightEnd = getBookingEndDate(right);

          return (rightEnd?.getTime() || 0) - (leftEnd?.getTime() || 0);
        })
        .slice(0, 20)
        .map((booking) => ({
          ...booking,
          aircraft:
            postFlightAircraftMap[
              String(booking.aircraft_id || '')
            ] || null,
        }));

      const latestCompletedPreviousFlight = sourceRows
        .filter((booking) => {
          const end =
            getBookingEndDate(booking);

          return Boolean(
            end && end < now
          );
        })
        .filter(
          (booking) =>
            !isBookingCancelledOrDeleted(
              booking
            )
        )
        .filter(
          (booking) =>
            getResolvedPostFlightStatus(
              booking
            ) === 'completed'
        )
        .sort((left, right) => {
          const leftEnd =
            getBookingEndDate(left);

          const rightEnd =
            getBookingEndDate(right);

          return (
            (rightEnd?.getTime() || 0) -
            (leftEnd?.getTime() || 0)
          );
        })[0] || null;

      const completedPreviousFlight =
        latestCompletedPreviousFlight
          ? {
              ...latestCompletedPreviousFlight,
              aircraft:
                postFlightAircraftMap[
                  String(
                    latestCompletedPreviousFlight
                      .aircraft_id || ''
                  )
                ] || null,
            }
          : null;

      if (!cancelled) {
        setPostFlightBacklog(
          outstandingPostFlights
        );

        setPreviousFlightBooking(
          completedPreviousFlight
        );
      }

      const {
        activeBooking,
        postCompleteBooking,
        nextFutureBooking,
        overdueBooking,
      } = getActiveNextOverdueCandidates(
        bookingRows || [],
        new Date()
      );

      const nextFlightBooking =
        activeBooking ||
        nextFutureBooking ||
        null;

      const { workflowBooking: flowBooking } =
        selectDashboardWorkflowBookings({
          activeBooking,
          nextFutureBooking,
          overdueBooking,
          postCompleteBooking,
        });

      console.log(
        '[TEMP][DashboardSelectionCandidates]',
        {
          activeBookingId: String(
            activeBooking?.id || ''
          ),
          nextFutureBookingId: String(
            nextFutureBooking?.id || ''
          ),
          postCompleteBookingId: String(
            postCompleteBooking?.id || ''
          ),
          overdueBookingId: String(
            overdueBooking?.id || ''
          ),
          selectedMainBookingId: String(
            nextFlightBooking?.id || ''
          ),
          selectedFlowBookingId: String(
            flowBooking?.id || ''
          ),
        }
      );

      if (!cancelled) {
        setWorkflowBooking(flowBooking || null);

        if (!nextFlightBooking) {
          setNextFlight(null);
          return;
        }

                let aircraftData = null;
        let previousPostFlightOutstanding = false;
        let previousPostFlightBookingId = '';

        const currentOrganisationId = String(
          data?.organisation_id ||
            user?.user_metadata?.organisation_id ||
            user?.app_metadata?.organisation_id ||
            ''
        ).trim();

        if (
          nextFlightBooking.aircraft_id &&
          currentOrganisationId
        ) {
          const { data: aircraft } = await supabase
            .from('aircraft')
            .select(
              'registration, icao_type, image_url'
            )
            .eq(
              'id',
              nextFlightBooking.aircraft_id
            )
            .eq(
              'organisation_id',
              currentOrganisationId
            )
            .maybeSingle();

          aircraftData = aircraft || null;
        }

        if (
          nextFlightBooking.aircraft_id &&
          nextFlightBooking.date
        ) {
          let previousResult = await supabase
            .from('bookings')
            .select(
              'id, user_id, aircraft_id, date, time, duration, end_date, end_time, briefing_data, tech_log_status'
            )
            .eq(
              'aircraft_id',
              nextFlightBooking.aircraft_id
            )
            .lte(
              'date',
              nextFlightBooking.date
            )
            .order('date', {
              ascending: false,
            })
            .limit(40);

          if (
            previousResult.error &&
            isMissingDashboardBookingColumnsError(
              previousResult.error
            )
          ) {
            const fallback = await supabase
              .from('bookings')
              .select(
                'id, user_id, aircraft_id, date, time, duration, briefing_data, tech_log_status'
              )
              .eq(
                'aircraft_id',
                nextFlightBooking.aircraft_id
              )
              .lte(
                'date',
                nextFlightBooking.date
              )
              .order('date', {
                ascending: false,
              })
              .limit(40);

            previousResult = {
              data: (fallback.data || []).map(
                (row) => ({
                  ...row,
                  end_date: null,
                  end_time: null,
                })
              ),
              error: fallback.error,
            };
          }

          if (!previousResult.error) {
            const nextFlightStart =
              parseDateTime(
                nextFlightBooking.date,
                nextFlightBooking.time
              );

            const now = new Date();

            const previousAircraftBooking = (
              previousResult.data || []
            )
              .filter(
                (row) =>
                  String(row.id) !==
                  String(nextFlightBooking.id)
              )
              .map((row) => ({
                booking: row,
                end: getBookingEndDate(row),
              }))
              .filter(
                ({ end }) =>
                  end &&
                  nextFlightStart &&
                  end < nextFlightStart &&
                  end <= now
              )
              .sort(
                (left, right) =>
                  right.end.getTime() -
                  left.end.getTime()
              )[0]?.booking;

            previousPostFlightOutstanding =
              Boolean(
                previousAircraftBooking &&
                  !isPostFlightCompleted(
                    previousAircraftBooking
                  )
              );

            previousPostFlightBookingId =
              previousPostFlightOutstanding
                ? String(
                    previousAircraftBooking?.id ||
                      ''
                  )
                : '';
          }
        }

        setNextFlight({
          ...nextFlightBooking,
          aircraft: aircraftData,
          previousPostFlightOutstanding,
          previousPostFlightBookingId,
        });
      }
    }

    resolveProfile();

    return () => {
      cancelled = true;
    };
  }, [
    location.key,
    user?.id,
    user?.user_metadata?.role,
    user?.app_metadata?.role,
    user?.role,
    userMeta.full_name,
    userMeta.phone,
    userMeta.licence_type,
    userMeta.home_airfield,
    userMeta.org_name,
    user?.user_metadata?.organisation_id,
    user?.app_metadata?.organisation_id,
  ]);

  const roleCandidates = collectRoles(resolvedRole, user?.user_metadata?.role, user?.app_metadata?.role, user?.role);
  const isOwner = roleCandidates.includes('owner') || isOwnerByEmail(user?.email);
  const isDelegatedAdmin = roleCandidates.includes('admin') && !isOwner;

  const selectedPackageLabel = getOrganisationPackageLabel(
    selectedOrganisationSubscriptionTier
  );

  let trialNoticeConfig = null;

  if (organisationTrialWarningLevel === 'seven-days') {
    trialNoticeConfig = {
      tone: 'sevenDays',
      title: 'Your Full Package trial ends soon',
      message: `You have ${organisationTrialDaysRemaining} days remaining. Your selected package is ${selectedPackageLabel}. Before the trial ends, you will be able to confirm this package, choose another package, or continue with Calendar booking free.`,
    };
  } else if (
    organisationTrialWarningLevel === 'three-days'
  ) {
    trialNoticeConfig = {
      tone: 'threeDays',
      title: `Your Full Package trial ends in ${organisationTrialDaysRemaining} days`,
      message: `Please confirm whether you wish to continue with ${selectedPackageLabel}. Without a paid subscription, Wingit will continue on Calendar booking free.`,
    };
  } else if (
    organisationTrialWarningLevel === 'expired'
  ) {
    trialNoticeConfig = {
      tone: 'expired',
      title: 'Your Full Package trial has ended',
      message: `Your organisation is now using Calendar booking free. Your selected package was ${selectedPackageLabel}. Paid package activation will restore the additional features.`,
    };
  }

  const showOwnerTrialNotice =
    isOwner &&
    organisationTrialWarningLevel !==
      'none' &&
    (
      organisationTrialWarningLevel ===
        'expired' ||
      organisationTrialActive
    );

  useEffect(() => {
    if (!user?.id) return;

    function refreshPilotStatus() {
      const settings =
        loadAppSettings(sessionOrganisationId);
      const fallbackCompliance = loadPilotCompliance(user.id);
      const compliance = { ...fallbackCompliance, ...dbCompliance };
      const summary = buildPilotStatusSummary(compliance, settings.operational || {});
      setStatusRows(summary.rows || []);
    }

    refreshPilotStatus();
    window.addEventListener('wingit-pilot-status-updated', refreshPilotStatus);
    window.addEventListener('wingit-settings-updated', refreshPilotStatus);

    return () => {
      window.removeEventListener('wingit-pilot-status-updated', refreshPilotStatus);
      window.removeEventListener('wingit-settings-updated', refreshPilotStatus);
    };
  }, [user?.id, dbCompliance, sessionOrganisationId]);

  useEffect(() => {
    let cancelled = false;

    async function fetchOwnerOperations() {
      if (!isOwner || !user?.id) {
        setOperations({
          openDefects: [],
          techOutstanding: [],
          paymentOutstanding: [],
          documentAttention: [],
          closed: [],
        });
        return;
      }

      setOperationsLoading(true);
      setOperationsError('');
const { data: ownerProfile, error: ownerProfileError } = await supabase
  .from('users')
  .select('organisation_id')
  .eq('id', user.id)
  .maybeSingle();

const organisationId = String(
  ownerProfile?.organisation_id || ''
).trim();

if (ownerProfileError || !organisationId) {
  setOperationsError('Could not identify the current organisation.');
  setOperationsLoading(false);
  return;
}

const {
  data: organisationMembers,
  error: organisationMembersError,
} = await supabase
  .from('users')
  .select('id, auth_id, full_name, email')
  .eq('organisation_id', organisationId);

if (organisationMembersError) {
  setOperationsError('Could not load organisation members.');
  setOperationsLoading(false);
  return;
}

const organisationBookingUserIds = [
  ...new Set(
    (organisationMembers || [])
      .flatMap((member) => [
        String(member?.id || '').trim(),
        String(member?.auth_id || '').trim(),
      ])
      .filter(Boolean)
  ),
];

if (organisationBookingUserIds.length === 0) {
  setOperations({
    openDefects: [],
    techOutstanding: [],
    paymentOutstanding: [],
    documentAttention: [],
    closed: [],
  });
  setOperationsLoading(false);
  return;
}
      const { data: bookingRows, error: bookingsError } = await supabase
        .from('bookings')
        .select('id, user_id, aircraft_id, date, time, duration, tech_log_status, payment_status, payment_amount, tech_log_completed_at, payment_completed_at')
        .in('user_id', organisationBookingUserIds)
        .order('date', { ascending: false })
        .order('time', { ascending: false })
        .limit(80);

      if (cancelled) return;

      if (bookingsError) {
        setOperationsError(`Could not load owner operations: ${bookingsError.message}`);
        setOperationsLoading(false);
        return;
      }

      const rows = bookingRows || [];
      const aircraftIds = [...new Set(rows.map(row => row.aircraft_id).filter(Boolean))];
      const bookingIds = rows.map(row => String(row.id));

      let aircraftMap = {};
      let userMap = {};
      let techLogMap = {};
      let documentAttention = [];

      if (aircraftIds.length > 0) {
        const { data: aircraftRows } = await supabase
  .from('aircraft')
  .select('id, registration, icao_type')
  .eq('organisation_id', organisationId)
  .in('id', aircraftIds);

        aircraftMap = (aircraftRows || []).reduce((map, aircraft) => {
          map[String(aircraft.id)] = aircraft;
          return map;
        }, {});

        const documentRowsByAircraft = await Promise.all(
          (aircraftRows || []).map(async (aircraft) => {
            try {
              const documents = await fetchAircraftDocuments(
                aircraft.id
              );
              const status = getAircraftDocumentStatus(
                documents || []
              );

              return (status.messages || []).map((message) => ({
                ...message,
                aircraft_id: aircraft.id,
                aircraftLabel: aircraft.registration
                  ? `${aircraft.registration}${aircraft.icao_type ? ` / ${aircraft.icao_type}` : ''}`
                  : `Aircraft ${aircraft.id || '-'}`,
              }));
            } catch (_error) {
              return [];
            }
          })
        );

        documentAttention = documentRowsByAircraft.flat();
      }

      userMap = (organisationMembers || []).reduce((map, member) => {
        const idKey = String(member?.id || '').trim();
        const authIdKey = String(member?.auth_id || '').trim();

        if (idKey) {
          map[idKey] = member;
        }

        if (authIdKey) {
          map[authIdKey] = member;
        }

        return map;
      }, {});

      if (bookingIds.length > 0) {
        const { data: techRows, error: techError } = await fetchOfficialTechLogsForBookings(bookingIds);

        if (!techError) {
          const indexed = indexOfficialTechLogs(techRows || []);
          techLogMap = indexed.logMap || {};
        }
      }

      if (cancelled) return;

      const enriched = rows.map((booking) => {
        const aircraft = aircraftMap[String(booking.aircraft_id)] || {};
        const member = userMap[String(booking.user_id)] || {};
        const techLog = techLogMap[String(booking.id)] || {};
                const closureState =
          getFlightClosureState(
            booking,
            techLog
          );

        return {
          ...booking,
          closureState,
          techLog,
          aircraftLabel: aircraft.registration
            ? `${aircraft.registration}${aircraft.icao_type ? ` / ${aircraft.icao_type}` : ''}`
            : `Aircraft ${booking.aircraft_id || '-'}`,
          pilotLabel: member.full_name || member.email || 'Unknown pilot',
        };
      });

      setOperations({
        openDefects: enriched.filter(row => hasOpenDefect(row.techLog)),
        techOutstanding: enriched.filter(row => row.closureState === 'tech_outstanding'),
        paymentOutstanding: enriched.filter(row => isOutstandingPaymentBooking(row, row.techLog)),
        documentAttention,
        closed: enriched.filter(row => row.closureState === 'closed'),
      });

      setOperationsLoading(false);
    }

    fetchOwnerOperations();

    return () => {
      cancelled = true;
    };
  }, [isOwner, user?.id]);
const profileIncomplete =
  !profileData.phone ||
  !profileData.licenceType ||
  !profileData.homeAirfield ||
  !dbCompliance.licenceExpiry ||
  !dbCompliance.medicalExpiry;

  const currentBooking = workflowBooking;
  const preflightData =
    currentBooking?.briefing_data?.preflight ||
    {};
  const preFlightChecks = {
    weatherReviewed:
      preflightData?.weatherReviewed === true,
    notamsReviewed:
      preflightData?.notamsReviewed === true,
    routePlanned:
      preflightData?.routePlanned === true,
    weightBalanceComplete:
      preflightData?.weightBalanceComplete === true,
    documentsCurrent:
      preflightData?.documentsCurrent === true,
  };

  const completedCheckCount = Object.values(
    preFlightChecks
  ).filter(Boolean).length;

  const isPreFlightCompleted =
    preflightData?.completed === true ||
    preflightData?.completionConfirmed === true;

  const preFlightProgressState =
    completedCheckCount === 0
      ? 'not_started'
      : isPreFlightCompleted
        ? 'completed'
        : 'action_required';

  useEffect(() => {
    console.log(
      'Dashboard preflight completion:',
      currentBooking?.briefing_data,
      isPreFlightCompleted,
      preFlightProgressState
    );
  }, [
    currentBooking,
    isPreFlightCompleted,
    preFlightProgressState,
  ]);

  async function handleLogout() {
    let signOutResult;
    try {
      signOutResult = await supabase.auth.signOut();
    } catch (error) {
      console.error('[Dashboard logout error]', error?.message || error);
      window.alert('Unable to log out. Please try again.');
      return;
    }

    if (signOutResult.error) {
      console.error(
        '[Dashboard logout error]',
        signOutResult.error?.message || signOutResult.error
      );
      window.alert('Unable to log out. Please try again.');
      return;
    }

    navigate('/login');
  }

  async function handleBookAircraftAction(
    preferredBookingId = ''
  ) {
    // Only a string is ever a valid booking ID; anything else (e.g. an accidental event object) means "no ID supplied".
    const requestedBookingId =
      typeof preferredBookingId === 'string'
        ? preferredBookingId.trim()
        : '';

    if (requestedBookingId) {
      navigate(
        `/book-aircraft?bookingId=${encodeURIComponent(requestedBookingId)}&origin=dashboard`
      );
      return;
    }

    if (!user?.id) {
      navigate('/book-aircraft');
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('bookings')
      .select('id, date, time')
      .eq('user_id', user.id)
      .gte('date', today)
      .order('date', { ascending: true })
      .order('time', { ascending: true })
      .limit(40);

    if (error) {
      navigate('/book-aircraft');
      return;
    }

    const now = new Date();
    const nextFutureBooking = (data || []).find((booking) => {
      const start = parseDateTime(booking.date, booking.time);
      return !!start && start > now;
    });

    if (nextFutureBooking?.id) {
      navigate(`/book-aircraft?bookingId=${encodeURIComponent(String(nextFutureBooking.id))}`);
      return;
    }

    navigate('/book-aircraft');
  }

  function openPostFlightForBooking(bookingId) {
    const id = String(bookingId || '').trim();

    if (!id) {
      return;
    }

    navigate(
      `/post-flight?bookingId=${encodeURIComponent(id)}`
    );
  }

  const previousFlightCard =
    selectPreviousFlightCard({
      postFlightBacklog,
      completedBooking:
        previousFlightBooking,
    });

  return (
  <div className="pilot-page pilot-dashboard" style={styles.shell}>
    <DashboardHero
      isMobile={isMobile}
      user={user}
      userMeta={userMeta}
      organisationId={sessionOrganisationId}
      profileData={profileData}
      membershipTier={organisationSubscriptionTier}
      handleLogout={handleLogout}
      onBookAircraft={handleBookAircraftAction}
    />

    {user?.id && (
      <OrganisationContextSelector userId={user.id} isMobile={isMobile} />
    )}

    <section
      aria-label="Organisation membership demonstration"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        flexWrap: 'wrap',
        marginBottom: '1rem',
        padding: isMobile ? '0.9rem' : '1rem 1.2rem',
        border: `1px solid ${wingitDesign.colors.border}`,
        borderRadius: wingitDesign.radius.medium,
        background: wingitDesign.colors.surface,
        textAlign: 'left',
      }}
    >
      <div>
        <div
          style={{
            color: wingitDesign.colors.primary,
            fontSize: '0.7rem',
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          Local demonstration
        </div>
        <strong style={{ display: 'block', marginTop: '0.25rem' }}>
          Explore organisation memberships
        </strong>
        <span
          style={{
            display: 'block',
            marginTop: '0.2rem',
            color: wingitDesign.colors.textMuted,
            fontSize: '0.85rem',
          }}
        >
          Sample data only; this does not change access to your Wingit organisation.
        </span>
      </div>
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
        <a
          href="/organisation-memberships"
          style={{
            ...wingitDesign.buttons.primary,
            display: 'inline-block',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          Manage memberships
        </a>
        <a
          href="/?demo=organisations"
          style={{
            ...wingitDesign.buttons.secondary,
            display: 'inline-block',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          Preview demonstration
        </a>
      </div>
    </section>

    {showOwnerTrialNotice &&
      trialNoticeConfig && (
        <section
          style={{
            ...styles.trialNotice,
            ...(styles.trialNoticeTone[
              trialNoticeConfig.tone
            ] || {}),
          }}
        >
          <h3 style={styles.trialNoticeTitle}>
            {trialNoticeConfig.title}
          </h3>

          <p style={styles.trialNoticeText}>
            {trialNoticeConfig.message}
          </p>
        </section>
      )}

    <main style={styles.mainGrid}>
      <DashboardNextFlightBrief
        isMobile={isMobile}
        nextFlight={nextFlight}
        navigate={navigate}
      />

      <DashboardFlightFlow
        isMobile={isMobile}
        membershipTier={organisationSubscriptionTier}
        workflowBooking={workflowBooking}
        isPreFlightCompleted={
          isPreFlightCompleted
        }
        preFlightProgressState={
          preFlightProgressState
        }
        navigate={navigate}
        onBookAircraft={handleBookAircraftAction}
        
      />

      <DashboardPreviousFlightBrief
        isMobile={isMobile}
        status={previousFlightCard.status}
        booking={previousFlightCard.booking}
        onCloseFlight={() =>
          openPostFlightForBooking(
            previousFlightCard.booking?.id
          )
        }
        onViewFlightDetails={() =>
          openPostFlightForBooking(
            previousFlightCard.booking?.id
          )
        }
      />


      <DashboardMainContent
        isMobile={isMobile}
        isOwner={isOwner}
        isDelegatedAdmin={isDelegatedAdmin}
        nextFlight={nextFlight}
        statusRows={statusRows}
        operations={operations}
        operationsLoading={operationsLoading}
        operationsError={operationsError}
        getStatusAppearance={getStatusAppearance}
        operationTone={operationTone}
        navigate={navigate}
        profileIncomplete={profileIncomplete}
      />

      <DashboardNoticeBoard
        isOwner={isOwner}
        user={user}
        organisationId={organisationId}
      />
    </main>
  </div>
);
}


const operationTone = {
  danger: {
    color: '#f26d6d',
    border: 'rgba(242,109,109,0.35)',
    background: 'rgba(242,109,109,0.08)',
  },
  warning: {
    color: '#f2b84b',
    border: 'rgba(242,184,75,0.35)',
    background: 'rgba(242,184,75,0.08)',
  },
  good: {
    color: '#2fd37d',
    border: 'rgba(47,211,125,0.35)',
    background: 'rgba(47,211,125,0.08)',
  },
  neutral: {
    color: '#9fb6d1',
    border: 'rgba(159,182,209,0.25)',
    background: 'rgba(159,182,209,0.06)',
  },
};

function getStyles(isMobile) {
  const ds = wingitDesign;

  return {
    shell: {
      maxWidth: 1180,
      margin: isMobile ? '1rem auto' : '2.5rem auto',
      padding: isMobile ? '0 0.75rem 1.5rem' : '0 1.2rem 2rem',
      color: ds.colors.text,
    },

    topBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '0.8rem',
      marginBottom: isMobile ? '1rem' : '1.5rem',
    },

    brandBlock: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '0.7rem' : '1rem',
      minWidth: 0,
    },

    logo: {
      width: isMobile ? 54 : 88,
      height: isMobile ? 54 : 88,
      objectFit: 'contain',
      filter: 'drop-shadow(0 10px 22px rgba(0,0,0,0.28))',
      flexShrink: 0,
    },

    brandTitle: {
      color: ds.colors.text,
      fontSize: isMobile ? '1.35rem' : '2.2rem',
      fontWeight: 900,
      letterSpacing: isMobile ? '0.09em' : '0.14em',
      lineHeight: 1,
    },

    brandSubtitle: {
      color: ds.colors.textMuted,
      fontSize: isMobile ? '0.58rem' : '0.82rem',
      fontWeight: 800,
      letterSpacing: isMobile ? '0.11em' : '0.2em',
      marginTop: 6,
    },

    logoutButton: {
      ...ds.buttons.ghost,
      color: '#ffb0b0',
      border: '1px solid rgba(242,109,109,0.35)',
      background: 'rgba(242,109,109,0.12)',
      padding: isMobile ? '0.5rem 0.7rem' : '0.65rem 1rem',
      fontSize: isMobile ? '0.74rem' : '0.86rem',
      flexShrink: 0,
    },

    hero: {
      ...ds.cards.primary,
      borderRadius: isMobile ? ds.radius.large : ds.radius.xl,
      padding: isMobile ? '1.1rem' : '1.7rem',
      marginBottom: '1rem',
      display: 'flex',
      justifyContent: 'space-between',
      gap: '1rem',
      alignItems: 'center',
      flexWrap: 'wrap',
    },

    eyebrow: {
      ...ds.typography.caption,
      color: ds.colors.primary,
      fontWeight: 900,
      marginBottom: '0.5rem',
    },

    heroTitle: {
      color: ds.colors.text,
      margin: 0,
      fontSize: isMobile ? '1.45rem' : '2rem',
      lineHeight: 1.16,
      fontWeight: 850,
    },

    heroMeta: {
      marginTop: '0.65rem',
      display: 'flex',
      gap: '0.45rem',
      flexWrap: 'wrap',
      color: ds.colors.textSoft,
      fontSize: isMobile ? '0.82rem' : '0.92rem',
    },

    heroActionBlock: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, auto)',
      gap: '0.65rem',
      width: isMobile ? '100%' : 'auto',
      marginTop: isMobile ? '0.5rem' : 0,
    },

    mainGrid: {
  display: 'grid',
  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
  gap: isMobile ? '0.75rem' : '1rem',
  alignItems: 'start',
},

    panel: {
      ...ds.cards.secondary,
      borderRadius: isMobile ? ds.radius.large : ds.radius.large,
      padding: isMobile ? '1rem' : '1.2rem',
      boxShadow: ds.shadow.card,
    },

    panelWide: {
      ...ds.cards.secondary,
      gridColumn: '1 / -1',
      borderRadius: isMobile ? ds.radius.large : ds.radius.large,
      padding: isMobile ? '1rem' : '1.2rem',
      boxShadow: ds.shadow.card,
    },

    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1rem',
      flexWrap: 'wrap',
      marginBottom: '0.9rem',
    },

    sectionTitle: {
      ...ds.typography.cardTitle,
      margin: 0,
      color: ds.colors.text,
      fontSize: isMobile ? '0.92rem' : '1rem',
      letterSpacing: '0.1em',
      fontWeight: 900,
    },

    sectionSubtle: {
      ...ds.typography.body,
      margin: '0.3rem 0 0',
      color: ds.colors.textMuted,
      fontSize: isMobile ? '0.84rem' : '0.92rem',
    },

    primaryButton: {
      ...ds.buttons.primary,
      fontSize: '0.88rem',
      width: isMobile ? '100%' : 'auto',
    },

    secondaryButton: {
      ...ds.buttons.secondary,
      fontSize: '0.86rem',
      width: isMobile ? '100%' : 'auto',
    },

    aircraftTitle: {
      color: ds.colors.text,
      fontSize: isMobile ? '1.35rem' : '1.65rem',
      fontWeight: 900,
      marginBottom: '0.8rem',
      letterSpacing: '0.03em',
    },

    nextFlightDate: {
      color: ds.colors.textSoft,
      fontSize: isMobile ? '1rem' : '1.12rem',
      fontWeight: 750,
      marginBottom: '0.3rem',
    },

    nextFlightTime: {
      color: ds.colors.text,
      fontSize: isMobile ? '1.7rem' : '2rem',
      fontWeight: 900,
      letterSpacing: '0.04em',
    },

    nextFlightPurpose: {
      ...ds.typography.caption,
      color: ds.colors.textMuted,
      fontWeight: 900,
      marginTop: '0.55rem',
    },

    statusGrid: {
      display: 'grid',
      gap: '0.6rem',
    },

    statusRow: {
      border: '1px solid',
      borderRadius: ds.radius.medium,
      padding: '0.7rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '0.7rem',
    },

    statusLabel: {
      color: ds.colors.text,
      fontSize: '0.82rem',
      fontWeight: 900,
      letterSpacing: '0.08em',
    },

    statusSummary: {
      color: ds.colors.textMuted,
      fontSize: '0.84rem',
      marginTop: 3,
    },

    statusPill: {
      border: '1px solid',
      borderRadius: 999,
      padding: '0.32rem 0.5rem',
      fontSize: '0.68rem',
      fontWeight: 900,
      whiteSpace: 'nowrap',
    },

    operationsSummaryGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))',
      gap: '0.7rem',
    },

    operationsSummaryCard: {
      border: '1px solid',
      borderRadius: ds.radius.medium,
      padding: isMobile ? '0.8rem' : '1rem',
      textAlign: 'left',
      cursor: 'pointer',
      transition: ds.transition.normal,
    },

    operationsSummaryValueRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '1rem',
    },

    operationsSummaryLabel: {
      ...ds.typography.caption,
      color: ds.colors.text,
      fontWeight: 900,
    },

    operationsSummaryValue: {
      fontSize: isMobile ? '1.7rem' : '2.1rem',
      fontWeight: 950,
      lineHeight: 1,
    },

    operationsSummaryHelper: {
      color: ds.colors.textMuted,
      fontSize: '0.82rem',
      marginTop: '0.35rem',
      lineHeight: 1.35,
    },

    actionsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(190px, 1fr))',
      gap: '0.75rem',
    },

    actionTile: {
      ...ds.cards.secondary,
      background: ds.colors.surface,
      color: ds.colors.text,
      borderRadius: ds.radius.medium,
      padding: isMobile ? '0.95rem' : '1.25rem',
      minHeight: isMobile ? 88 : 118,
      textAlign: 'left',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      transition: ds.transition.normal,
    },

    actionTitle: {
      color: ds.colors.text,
      fontSize: '0.95rem',
      fontWeight: 900,
      marginBottom: '0.45rem',
      letterSpacing: '0.05em',
    },

    actionDescription: {
      color: ds.colors.textMuted,
      fontSize: '0.84rem',
      lineHeight: 1.35,
    },

    emptyText: {
      color: ds.colors.textMuted,
      fontSize: '0.95rem',
      lineHeight: 1.45,
    },

    errorBox: {
      ...ds.chips.danger,
      borderRadius: ds.radius.medium,
      padding: '0.8rem',
      fontSize: '0.92rem',
    },

    trialNotice: {
      ...ds.cards.secondary,
      gridColumn: '1 / -1',
      borderRadius: ds.radius.large,
      padding: isMobile ? '0.85rem 0.95rem' : '0.95rem 1.05rem',
      marginBottom: isMobile ? '0.8rem' : '0.95rem',
      border: '1px solid rgba(242,184,75,0.35)',
      background: 'rgba(242,184,75,0.08)',
    },

    trialNoticeTone: {
      sevenDays: {
        border: '1px solid rgba(242,184,75,0.42)',
        background: 'rgba(242,184,75,0.09)',
      },
      threeDays: {
        border: '1px solid rgba(242,132,75,0.52)',
        background: 'rgba(242,132,75,0.10)',
      },
      expired: {
        border: '1px solid rgba(242,109,109,0.42)',
        background: 'rgba(242,109,109,0.10)',
      },
    },

    trialNoticeTitle: {
      margin: 0,
      color: ds.colors.text,
      fontSize: isMobile ? '0.94rem' : '1rem',
      fontWeight: 800,
      lineHeight: 1.25,
    },

    trialNoticeText: {
      margin: '0.4rem 0 0',
      color: ds.colors.textSoft,
      fontSize: isMobile ? '0.83rem' : '0.89rem',
      lineHeight: 1.45,
    },
  };
}